import * as React from 'react';
import { connect } from 'react-redux';
import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';
import DeliverableTagPill from 'components/deliverable-tag-pill/DeliverableTagPill';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';
import MediaPreview from 'components/MediaPreview';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import OnsiteContentCard from 'components/cards/onsite-content-card/OnsiteContentCard';
import PageSection from 'components/page/page-section/PageSection';
import Pagination from 'components/pagination/Pagination';
import Popover from 'components/popover/Popover';
import SortableTable from 'components/sortable-table/SortableTable';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import ActionLink from 'components/buttons/action-link/ActionLink';

import { DELIVERABLE_ICONS, DELIVERABLE_TYPES, DELIVERABLE_TYPE_INSTAGRAM_STORY } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';
import { dataProvider, getCreatorColumn, getMetricsColumns, getSortedData } from '../../utils/onsite-performance';
import { getChannelByContentType } from 'utils/reporting';
import { TASK_TAGS_COLORS, TASK_TAGS_COLORS_REVERSE } from 'utils/subcampaign';

import './onsite-performance-by-task-table.scss';

applyTranslations(require('./onsite-performance-by-task-table.i18n.json'));

import type {
  ContentCardItem,
  DeliverableTag,
  DeliverableTagColor,
  InfluencerCommon,
  LocationWindow,
  MixedSelectDropdownItem,
  SortableTableColumnType,
  SortableTableSort
} from 'types';
import type { OnsiteTaskPerformance } from '../../types/OnsitePerformance.type';

type MapByInfluencerId = {
  [x: number]: InfluencerCommon
};

const ITEMS_PER_PAGE = 200;

const resetButton = {
  label: __t('onsite_performance_by_task_table.buttons_all_creators'),
  icon: 'users-connected-white-bg-custom-fg'
};

const getPostPopover = (post: ContentCardItem, creator: InfluencerCommon) => (
  <ContentCardContext.Provider value={{ posts: [post] }}>
    <OnsiteContentCard
      key={post.id}
      item={post}
      index={0}
      channel={getChannelByContentType(post.type)}
      creatorProfile={creator}
    />
  </ContentCardContext.Provider>
);

const getDeliverableTag = (tag?: DeliverableTag): React.ReactNode => {
  if (!tag || !tag.name || !tag.color) {
    return null;
  }

  return (
    <div className="onsite-performance-by-task-table__deliverable-tag">
      <DeliverableTagPill
        name={tag.name}
        color={tag.color}
      />
    </div>
  );
};

const getColumns = (influencersMapById: MapByInfluencerId): Array<SortableTableColumnType> => [
  getCreatorColumn(influencersMapById),
  {
    title: __t('onsite_performance_by_task_table.column.tasks'),
    key: 'tasks',
    width: 220,
    render: (col: OnsiteTaskPerformance) => (
      <div className="onsite-performance-by-task-table__deliverable">
        <div className="onsite-performance-by-task-table__deliverable-icon">
          <SVGDynamicIcon
            name={`${DELIVERABLE_ICONS[camelCase(col.deliverableType)]}`}
            width={14}
            height={14}
            customColor="grey"
          />
        </div>
        <Tooltip
          message={col.taskName}
          display="flex"
          classNameWrapper="onsite-performance-by-task-table__deliverable-title-wrapper"
        >
          <div className="onsite-performance-by-task-table__deliverable-title text-overflow-ellipsis">
            {col.taskName}
          </div>
        </Tooltip>
        { getDeliverableTag(col.tag) }
      </div>
    )
  },
  {
    title: __t('onsite_performance_by_task_table.column.post'),
    key: 'post',
    width: 48,
    render: (col: OnsiteTaskPerformance) => {
      const thumbnailUrl = col.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY ?
        col.post.postAttachments[0]?.url
        : col.post.thumbnailUrl;

      const triggerComponent = thumbnailUrl ? (
        <MediaPreview
          width={32}
          height={32}
          attachment={{
            id: 1,
            url: thumbnailUrl,
            externalUrl: col.post.url ?? '',
            format: 'image'
          }}
        />
      ) : 'n/a';

      return (
        <Popover
          triggerComponent={triggerComponent}
          contentType="clear"
        >
          {getPostPopover(col.post, influencersMapById[col.influencerId])}
        </Popover>
      );
    }
  },
  {
    title: __t('onsite_performance_by_task_table.column.landing_page'),
    key: 'landing-page',
    width: 75,
    render: (col: OnsiteTaskPerformance) => {
      if (col.landingPages.length === 1) {
        return (
          <div className="onsite-performance-by-task-table__landing">
            <Tooltip
              message={__t('onsite_performance_by_task_table.landing_tooltip')}
              display="flex"
            >
              <SVGDynamicIcon
                name="link-external-custom-fg"
                width={15}
                height={15}
                customColor="grey"
                openInNewTab
                link={col.landingPages[0].baseUrl}
              />
            </Tooltip>
          </div>
        );
      }

      return (
        <div className="onsite-performance-by-task-table__landing">
          <Popover
            classNamePopover="onsite-performance-by-task-table__landing-page-popover"
            triggerComponent={
              <div className="onsite-performance-by-task-table__landing-page-trigger">
                <SVGDynamicIcon
                  name="link-external-custom-fg"
                  width={15}
                  height={15}
                  customColor="grey"
                />
                {col.landingPages.length ? `(${col.landingPages.length})` : ''}
              </div>
          }
            trigger="hover"
          >
            <div>
              {col.landingPages.map((landingPage, index) => (
                <div
                  className="onsite-performance-by-task-table__landing-page-link-container"
                  key={index}
                >
                  <span>{index + 1}.</span>
                  <ActionLink
                    className="onsite-performance-by-task-table__landing-page-link"
                    size="inherit"
                    color="dark"
                    openInNewTab
                    link={landingPage.baseUrl}
                  >
                    {landingPage.name || landingPage.baseUrl}
                  </ActionLink>
                </div>
              ))}
            </div>
          </Popover>
        </div>
      );
    }
  },
  ...getMetricsColumns([56, 60, 68, 62, 56, 82])
];

type OwnProps = {
  influencersMapById: MapByInfluencerId,
  influencerProfiles: Array<InfluencerCommon>,
  performanceByTask: Array<OnsiteTaskPerformance>
};

type StateProps = {
  location: LocationWindow
};

type Props = OwnProps & StateProps;

type State = {
  page: number,
  selectedInfluencerId: number | null,
  selectedDeliverableType: string,
  tags: Array<MixedSelectDropdownItem>,
  types: Array<MixedSelectDropdownItem>,
  sortingOptions: SortableTableSort
}

class OnsitePerformanceByTaskTable extends React.Component<Props, State> {
  state: State = {
    page: 1,
    selectedInfluencerId: null,
    selectedDeliverableType: '',
    tags: this.getTags(),
    types: this.getDeliverableTypesDropdownData(),
    sortingOptions: {
      sortingKey: 'gmv',
      ascending: false
    }
  };

  getTags(): Array<MixedSelectDropdownItem> {
    const tags = this.props.performanceByTask.reduce((res, t) => {
      if (t.tag && t.tag.name) {
        res.push({
          name: t.tag.name,
          color: t.tag.color
        });
      }

      return res;
    }, [] as Array<{ name: string, color: DeliverableTagColor }>);

    return uniqWith(tags, isEqual).map((tag, index) => ({
      id: index,
      identifier: `${tag.color}-${tag.name}`,
      label: tag.name,
      isSelected: false,
      isDisabled: false,
      isFocused: false,
      value: tag.name,
      color: TASK_TAGS_COLORS[tag.color || '']
    }));
  }

  getDeliverableTypesDropdownData(): Array<MixedSelectDropdownItem> {
    const tasksTypes = this.props.performanceByTask.map(t => t.deliverableType);
    const deliverableTypes = DELIVERABLE_TYPES.filter(type => tasksTypes.some(tasksType => tasksType === type));

    return deliverableTypes.map(type => ({
      identifier: type,
      isDisabled: false,
      isSelected: false,
      value: type,
      label: __t(`onsite_performance_by_task_table.tasks_types.${type}`)
    }));
  }

  handleInfluencerSelect = (selectedInfluencerId?: number | null) => {
    if (selectedInfluencerId !== this.state.selectedInfluencerId) {
      this.setState({
        selectedInfluencerId: selectedInfluencerId ?? null,
        page: 1
      });
    }
  };

  handleTaskTypeChange = (selectedDeliverableType?: string | null) => {
    if (selectedDeliverableType !== this.state.selectedDeliverableType) {
      this.setState({
        selectedDeliverableType: selectedDeliverableType || '',
        page: 1
      });
    }
  };

  handleTaskTagChange = (updatedList: Array<MixedSelectDropdownItem>): void => this.setState({
    tags: updatedList,
    page: 1
  });

  handleSortChange = (sortingOptions: SortableTableSort): void => this.setState({
    sortingOptions,
    page: 1
  });

  handlePageChange = (page: number) => this.setState({ page });

  getFilteredData(data: Array<OnsiteTaskPerformance>): Array<OnsiteTaskPerformance> {
    const { selectedInfluencerId, selectedDeliverableType, tags } = this.state;
    const filteredByInfluencer = selectedInfluencerId ?
      data.filter(t => t.influencerId === selectedInfluencerId)
      : data;

    const filteredByDeliverableType = selectedDeliverableType ?
      filteredByInfluencer.filter(t => t.deliverableType === selectedDeliverableType)
      : filteredByInfluencer;

    const selectedTags = tags.reduce((tagsStr, currentTag) => {
      if (currentTag.isSelected && currentTag.color) {
        tagsStr.push(`${currentTag.value}-${TASK_TAGS_COLORS_REVERSE[currentTag.color]}`);
      }

      return tagsStr;
    }, [] as Array<string>);


    if (!selectedTags.length) {
      return filteredByDeliverableType;
    }

    return filteredByDeliverableType.filter(t =>
      t.tag?.name && selectedTags.includes(`${t.tag.name}-${t.tag.color}`));
  }

  getSortedData(data: Array<OnsiteTaskPerformance>): Array<OnsiteTaskPerformance> {
    const { sortingOptions } = this.state;
    const { influencersMapById } = this.props;

    return getSortedData<OnsiteTaskPerformance>(data, influencersMapById, sortingOptions);
  }

  renderInfluencerSelector(): React.ReactNode {
    const { selectedInfluencerId } = this.state;
    const { influencerProfiles } = this.props;

    return (
      <PageSection
        style="volume"
        padding="none"
        margin="none"
        border="none"
      >
        <div className="onsite-performance-by-task-table__influencer-selector">
          <InfluencerSelector
            selectedInfluencerProfileId={selectedInfluencerId}
            influencerProfiles={influencerProfiles}
            shouldSort={false}
            resetButton={resetButton}
            onSelect={this.handleInfluencerSelect}
            showSearch
          />
        </div>
      </PageSection>
    );
  }

  renderFilters() {
    const { selectedDeliverableType, tags, types } = this.state;
    return (
      <div className="onsite-performance-by-task-table__filters">
        <div className="onsite-performance-by-task-table__control">
          <div className="onsite-performance-by-task-table__select-label">
            {__t('onsite_performance_by_task_table.tag_filter.label')}
          </div>
          <MixedSelectDropdown
            items={tags}
            name="onsite-performance-by-task-table-tag-filter"
            withMulti
            withColors
            withCheckboxes
            withSortable
            withRemovable
            withSearchable
            placeholder={__t('onsite_performance_by_task_table.tag_filter.placeholder')}
            onItemsToggle={this.handleTaskTagChange}
            onItemsRemove={this.handleTaskTagChange}
            onItemsRemoveAll={this.handleTaskTagChange}
            className="onsite-performance-by-task-table__select"
          />
        </div>
        <div className="onsite-performance-by-task-table__control">
          <div className="onsite-performance-by-task-table__select-label">
            {__t('onsite_performance_by_task_table.task_filter.label')}
          </div>
          <MixedSelectDropdown
            items={types.map(type => ({
              ...type,
              isSelected: selectedDeliverableType === type.value
            }))}
            name="onsite-performance-by-task-table-task-filter"
            withRemovable
            onItemsToggle={(updatedItems, data, selectedItem) => this.handleTaskTypeChange(selectedItem?.value)}
            onItemsRemoveAll={() => this.handleTaskTypeChange()}
            placeholder={__t('onsite_performance_by_task_table.task_filter.placeholder')}
            className="onsite-performance-by-task-table__select"
          />
        </div>
      </div>
    );
  }

  renderContent(): React.JSX.Element {
    const { influencersMapById, location, performanceByTask } = this.props;
    const { page, sortingOptions } = this.state;
    const data = this.getFilteredData(performanceByTask);

    const pagination = dataProvider({
      page,
      limit: ITEMS_PER_PAGE,
      data: this.getSortedData(data)
    });

    return (
      <React.Fragment>
        <PageSection
          style="volume"
          padding="none"
          margin="none"
          border="none"
        >
          <SortableTable
            data={pagination.data}
            noScroll
            columns={getColumns(influencersMapById)}
            defaultSortingKey={sortingOptions.sortingKey}
            defaultSortingDirection={sortingOptions.ascending ? 'ascending' : 'descending'}
            layout="dense"
            customClass="onsite-performance-by-task-table__content"
            onSortChanged={this.handleSortChange}
          />
        </PageSection>
        <Pagination
          key="onsite-performance-by-creator-table-pagination"
          location={location}
          meta={pagination.meta}
          links={pagination.links}
          onClick={(updatedPage: number) => this.handlePageChange(updatedPage)}
        />
      </React.Fragment>
    );
  }

  render() {
    return (
      <div>
        {this.renderInfluencerSelector()}
        {this.renderFilters()}
        {this.renderContent()}
      </div>
    );
  }
}

const mapStateToProps = ({ router }): StateProps => ({
  location: router.location
});

export default connect(mapStateToProps)(OnsitePerformanceByTaskTable);
